import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import Title from '../../components/Layout/Title';
import PacienteHeader from '../../components/Prontuario/PacienteHeader';
import GoBackButton from '../../components/Common/GoBackButton';
import ProntuarioTabs from '../../components/Prontuario/ProntuarioTabs';
import Alert from '../../components/UI/Alert';
import PacienteFormContent from '../../components/Pacientes/PacienteFormContent';
import { ProntuarioFilterProvider } from '../../context/ProntuarioFilterContext';

/**
 * Página principal do Prontuário do Paciente
 * 
 * Exibe informações completas do paciente, incluindo:
 * - Cabeçalho com dados básicos e KPIs
 * - Navegação por abas (Visão Geral, Histórico, Medicações, etc.)
 * - Modal de edição de paciente
 */
const PacienteProntuarioPage = () => {
    const { pacienteid } = useParams();
    const pacienteId = parseInt(pacienteid, 10);

    // Estados para gerenciamento do modal de edição e refresh dos componentes
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingPaciente, setEditingPaciente] = useState(null);
    const [refreshCounter, setRefreshCounter] = useState(0);

    /**
     * Reseta o scroll para o topo da página quando a página é montada ou o pacienteId muda
     * Isso garante que ao navegar para o prontuário, a página sempre comece no topo
     */
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [pacienteId]);

    /**
     * Abre o modal de edição do paciente
     * @param {Object|null} paciente - Dados do paciente a serem editados
     */
    const handleOpenEdit = useCallback((paciente = null) => {
        setEditingPaciente(paciente);
        setIsEditOpen(true);
    }, []);

    /**
     * Fecha o modal de edição e força refresh dos componentes dependentes
     */
    const handleCloseEdit = useCallback(() => {
        setIsEditOpen(false);
        setEditingPaciente(null);
        // Força recarregar dados do cabeçalho/abas após salvar
        setRefreshCounter(prev => prev + 1);
    }, []);

    // Validação do ID do paciente
    if (isNaN(pacienteId) || pacienteId <= 0) {
        return (
            <DashboardLayout>
                <div className="p-4 md:p-6 max-w-7xl mx-auto">
                    <Title text="Prontuário do Paciente" />
                    <div className="mt-4">
                        <GoBackButton />
                        <Alert
                            type="error"
                            message="Erro: ID do paciente não encontrado ou inválido na URL."
                        />
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <div className="w-full min-w-0 overflow-x-hidden p-4 md:p-6">
            <div className="max-w-7xl mx-auto w-full min-w-0">
                {/* Cabeçalho com Informações do Paciente e KPIs */}
                <div className="mb-2 sm:mb-3 min-w-0">
                    <PacienteHeader
                        key={`header-${refreshCounter}`}
                        pacienteId={pacienteId}
                        onEditPaciente={handleOpenEdit}
                    />
                </div>

                {/* Área de Navegação por Abas e Conteúdo */}
                <div className="mt-2 sm:mt-3 min-w-0 w-full">
                    <ProntuarioFilterProvider>
                        <ProntuarioTabs
                            key={`tabs-${refreshCounter}`}
                            pacienteId={pacienteId}
                        />
                    </ProntuarioFilterProvider>
                </div>

                {/* Modal de Edição do Paciente */}
                <PacienteFormContent
                    isOpen={isEditOpen}
                    onClose={handleCloseEdit}
                    pacienteData={editingPaciente}
                />
            </div>
        </div>
    );
};

export default PacienteProntuarioPage;